import Pusher from 'pusher-js';

// Pusher configuration
const PUSHER_KEY = 'b226472c7e1a9701c80a';
const PUSHER_CLUSTER = 'us2';

let pusherClient: Pusher | null = null;

export function getPusherClient(): Pusher {
  if (!pusherClient) {
    pusherClient = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
    });
  }
  return pusherClient;
}

// Step-based progress event - frontend derives completed/pending from position
interface ReviewStepEvent {
  step: string; // ReviewStepKey value
}

// Empty payload - frontend fetches state via API
interface ReviewReadyEvent {}

// Empty payload - frontend fetches state via API
interface ReviewFailedEvent {}

export function subscribeToExportReview(
  exportId: string,
  callbacks: {
    onStep?: (data: ReviewStepEvent) => void;
    onReady?: (data: ReviewReadyEvent) => void;
    onFailed?: (data: ReviewFailedEvent) => void;
  }
) {
  const client = getPusherClient();
  // Using public channel for Phase 1 (no auth required).
  // Phase 2 will use private-export-{id} with Pusher auth endpoint.
  const channelName = `export-${exportId}`;
  const channel = client.subscribe(channelName);

  const bindEventHandlers = () => {
    if (callbacks.onStep) {
      channel.bind('review:step', callbacks.onStep);
    }
    if (callbacks.onReady) {
      channel.bind('review:ready', callbacks.onReady);
    }
    if (callbacks.onFailed) {
      channel.bind('review:failed', callbacks.onFailed);
    }
  };

  // If channel is already subscribed, bind immediately
  // Otherwise wait for subscription to succeed
  if (channel.subscribed) {
    bindEventHandlers();
  } else {
    channel.bind('pusher:subscription_succeeded', bindEventHandlers);
  }

  // Return unsubscribe function
  return () => {
    channel.unbind_all();
    client.unsubscribe(channelName);
  };
}

// Export execution events

// Step-based progress event for execution
interface ExecuteStepEvent {
  step: 'PREPARING' | 'EXPORTING' | 'FINALIZING';
}

// Progress event for granular updates during export
interface ExecuteProgressEvent {
  processed: number;
  total: number;
}

// Empty payload - frontend fetches state via API
interface ExecuteCompletedEvent {}

// Empty payload - frontend fetches state via API
interface ExecuteFailedEvent {}

export function subscribeToExportExecution(
  exportId: string,
  callbacks: {
    onStep?: (data: ExecuteStepEvent) => void;
    onProgress?: (data: ExecuteProgressEvent) => void;
    onCompleted?: (data: ExecuteCompletedEvent) => void;
    onFailed?: (data: ExecuteFailedEvent) => void;
  }
) {
  const client = getPusherClient();
  const channelName = `export-${exportId}`;
  const channel = client.subscribe(channelName);

  const bindEventHandlers = () => {
    if (callbacks.onStep) {
      channel.bind('export:step', callbacks.onStep);
    }
    if (callbacks.onProgress) {
      channel.bind('export:progress', callbacks.onProgress);
    }
    if (callbacks.onCompleted) {
      channel.bind('export:completed', callbacks.onCompleted);
    }
    if (callbacks.onFailed) {
      channel.bind('export:failed', callbacks.onFailed);
    }
  };

  // If channel is already subscribed, bind immediately
  // Otherwise wait for subscription to succeed
  if (channel.subscribed) {
    bindEventHandlers();
  } else {
    channel.bind('pusher:subscription_succeeded', bindEventHandlers);
  }

  // Return unsubscribe function
  return () => {
    channel.unbind_all();
    client.unsubscribe(channelName);
  };
}

// Match run events

interface MatchRunStepEvent {
  step: 'FETCHING' | 'MATCHING' | 'FINALIZING';
}

interface MatchRunProgressEvent {
  step: 'FETCHING' | 'MATCHING';
  processed: number;
  /** Absent while fetching - the portal's size is not known until paging finishes. */
  total?: number;
  matched?: number;
}

/**
 * Subscribe to progress for one match run.
 *
 * Progress is also persisted on the run itself and the page polls as a fallback, so a
 * dropped event costs latency rather than correctness. A scan can run for tens of minutes,
 * which is long enough that relying on a single terminal event would be unwise.
 */
export function subscribeToMatchRun(
  runId: string,
  callbacks: {
    onStep?: (data: MatchRunStepEvent) => void;
    onProgress?: (data: MatchRunProgressEvent) => void;
    onCompleted?: () => void;
    onFailed?: () => void;
  }
) {
  const client = getPusherClient();
  const channelName = `match-run-${runId}`;
  const channel = client.subscribe(channelName);

  const bindEventHandlers = () => {
    if (callbacks.onStep) {
      channel.bind('match-run:step', callbacks.onStep);
    }
    if (callbacks.onProgress) {
      channel.bind('match-run:progress', callbacks.onProgress);
    }
    if (callbacks.onCompleted) {
      channel.bind('match-run:completed', callbacks.onCompleted);
    }
    if (callbacks.onFailed) {
      channel.bind('match-run:failed', callbacks.onFailed);
    }
  };

  // A run started from the create page fires events before the detail page mounts, so
  // bind immediately when the channel is already up.
  if (channel.subscribed) {
    bindEventHandlers();
  } else {
    channel.bind('pusher:subscription_succeeded', bindEventHandlers);
  }

  return () => {
    channel.unbind_all();
    client.unsubscribe(channelName);
  };
}

// Activity Feed events

export interface ActivityFeedItem {
  id: string;
  eventType: string;
  entityType: string | null;
  entityId: string | null;
  payload: unknown;
  deliveredAt: string;
}

export function subscribeToActivityFeed(
  callbacks: {
    onNewItem?: (data: ActivityFeedItem) => void;
  }
) {
  const client = getPusherClient();
  const channelName = 'activity-feed';
  const channel = client.subscribe(channelName);

  const bindEventHandlers = () => {
    if (callbacks.onNewItem) {
      channel.bind('new-item', callbacks.onNewItem);
    }
  };

  if (channel.subscribed) {
    bindEventHandlers();
  } else {
    channel.bind('pusher:subscription_succeeded', bindEventHandlers);
  }

  return () => {
    channel.unbind_all();
    client.unsubscribe(channelName);
  };
}
